import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {AlertController, ModalController} from '@ionic/angular';
import {OrganizzazioniService} from '../../_services/organizzazioni.service';
import {AccountOrganizzazione} from '../../_models/AccountOrganizzazione';
import {SaveMessage} from '../../_helpers/save-message';
import {constants} from '../../_helpers/constants';
import {AuthenticationService} from '../../_services/authentication.service';
import {LicenzeService} from '../../_services/licenze.service';
import {CampiUtenteAccountComponent} from '../campi-utente-account/campi-utente-account.component';
import {isQuantitaFissaSenzaConferme} from '../../_helpers/avviso-quantita-fissa';

@Component({
    selector: 'app-filtro-ordine',
    templateUrl: './filtro-ordine.component.html',
    styleUrls: ['./filtro-ordine.component.scss'],
    standalone: false
})
export class FiltroOrdineComponent  implements OnInit, OnDestroy {
  subscription: Subscription;
  accountOrganizzazione: AccountOrganizzazione;
  loading = false;
  // Sezione campi utente (feature 136): nascosta finché non si sa che l'organizzazione ne ha
  // definiti, così che senza definizioni il modale resti quello di prima (US2 §3).
  campiUtenteDisponibili = false;
  @ViewChild(CampiUtenteAccountComponent) campiUtente?: CampiUtenteAccountComponent;
  // Il modale modifica una copia: le opzioni in sessione cambiano solo con SALVA. Lo snapshot serve
  // a capire se ci sono modifiche da perdere alla chiusura.
  private snapshot = '';
  protected readonly constants = constants;
  constructor(
    public authService: AuthenticationService,
    public licenseService: LicenzeService,
    private saveMessage: SaveMessage,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private organizzazioneService: OrganizzazioniService) { }

  /** Avviso di combinazione per il campo quantità in Fisso (feature 150): non blocca il salvataggio. */
  get avvisoQuantitaFissa(): boolean {
    return isQuantitaFissaSenzaConferme(this.accountOrganizzazione);
  }

  ngOnInit() {
    this.caricaCopia(this.organizzazioneService.currentAccountSettingsSubject.value);
    this.subscription = this.organizzazioneService.currentAccountSettingsSubject
      .subscribe((_accountOrg: AccountOrganizzazione) => {
        if (_accountOrg) {
          this.caricaCopia(_accountOrg);
        }
      });
  }

  /** true se opzioni o campi utente differiscono da quanto salvato. */
  get modificato(): boolean {
    return FiltroOrdineComponent.perConfronto(this.accountOrganizzazione)
        !== FiltroOrdineComponent.perConfronto(JSON.parse(this.snapshot || 'null'))
      || !!this.campiUtente?.isDirty();
  }

  /** Proiezione per il confronto: un campo svuotato ('') equivale a uno mai valorizzato (null). */
  private static perConfronto(accountOrg: AccountOrganizzazione | null): string {
    const out: Record<string, string> = {};
    for (const [chiave, valore] of Object.entries(accountOrg ?? {}).sort(([a], [b]) => a.localeCompare(b))) {
      if (valore === undefined || valore === null || valore === '') continue;
      out[chiave] = typeof valore === 'object' ? JSON.stringify(valore) : String(valore);
    }
    return JSON.stringify(out);
  }

  private caricaCopia(accountOrg: AccountOrganizzazione) {
    this.snapshot = JSON.stringify(accountOrg);
    this.accountOrganizzazione = accountOrg ? JSON.parse(this.snapshot) as AccountOrganizzazione : accountOrg;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
  // Con modifiche non salvate chiede conferma; uscendo la copia viene scartata e le opzioni restano
  // quelle salvate.
  cancel = async () => {
    if (!this.modificato) {
      return this.modalCtrl.dismiss(false, 'cancel');
    }
    const alert = await this.alertCtrl.create({
      header: 'Modifiche non salvate',
      message: 'Le opzioni modificate non sono state salvate. Uscendo tornano come prima.',
      buttons: [
        {text: 'Resta', role: 'cancel'},
        {text: 'Esci senza salvare', role: 'destructive'}
      ]
    });
    await alert.present();
    const {role} = await alert.onDidDismiss();
    return role === 'destructive' ? this.modalCtrl.dismiss(false, 'cancel') : false;
  };

  // Campi utente PRIMA, preferenze DOPO (FR-019 / research D18). I campi utente sono l'unica delle
  // due scritture che può fallire per validazione: un 400 mostra il messaggio e ABORTISCE senza
  // toccare le preferenze, così l'esito è binario — o si scrive tutto, o nulla. L'ordine inverso
  // lascerebbe metà modale persistita e metà no. Eseguire il save preferenze dopo è sicuro: quel
  // percorso ignora CampiUtente e non può sovrascrivere quanto appena scritto.
  succesSave = async () => {
    this.loading = true;
    try {
      if (!await this.salvaCampiUtente()) return;
      const _accountOrg = await this.organizzazioneService
        .saveAccountOrganizzazioneAsync(this.accountOrganizzazione);
      // Riflette in sessione le preferenze appena salvate: comportamento preesistente e necessario.
      // FR-013 vieta la scrittura della cache dal percorso dedicato dei campi utente, non qui.
      this.organizzazioneService.setCurrentAccountSettingsValue(_accountOrg);
      this.modalCtrl.dismiss(true, 'confirm');
    } catch {
      this.saveMessage.messageError("Ci sono problemi con il salvataggio.");
    } finally {
      this.loading = false;
    }
  };

  // Senza definizioni non c'è nulla da salvare: il modale si comporta come prima della feature.
  private async salvaCampiUtente(): Promise<boolean> {
    if (!this.campiUtente?.hasDefinizioni) return true;
    return this.campiUtente.save();
  }
}
